import React from 'react';
import { GripVertical, Trash2 } from 'lucide-react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { OfferAttachment } from '../../../types';
import { offerAttachmentFileSrc } from '../utils/offerFirstPageImages';
import { OfferAttachmentPdfThumb } from './OfferAttachmentPdfThumb';

type Props = {
  leadRouteId: string;
  attachment: OfferAttachment;
  busy?: boolean;
  onRemove: (attachment: OfferAttachment) => void;
};

export function SortableOfferImageTile({ leadRouteId, attachment, busy = false, onRemove }: Props) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: attachment.id,
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 2 : undefined,
    opacity: isDragging ? 0.92 : 1,
  };
  const isPdf = attachment.mime_type === 'application/pdf';
  return (
    <li ref={setNodeRef} style={style} className="group relative list-none">
      <div className="aspect-[3/2] w-full overflow-hidden rounded-md bg-app-surface-muted">
        {isPdf ? (
          <OfferAttachmentPdfThumb leadRouteId={leadRouteId} attachment={attachment} />
        ) : (
          <img
            src={offerAttachmentFileSrc(leadRouteId, attachment.object_path)}
            alt={attachment.filename}
            className="h-full w-full object-cover"
          />
        )}
      </div>
      {isPdf ? (
        <div className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent px-1.5 pb-1 pt-5">
          <p className="truncate text-[10px] font-medium text-white">
            <span className="mr-1 rounded-[3px] bg-white/20 px-1 py-px text-[9px] font-semibold tracking-wide">
              PDF
            </span>
            {attachment.filename}
          </p>
        </div>
      ) : null}
      <div className="absolute left-1 top-1 flex gap-0.5 rounded-md bg-white/90 p-0.5 shadow-sm">
        <button
          type="button"
          className="inline-flex h-6 w-6 items-center justify-center rounded-md text-slate-400 hover:bg-app-hover hover:text-app-text cursor-grab active:cursor-grabbing touch-none"
          title="Ziehen zum Sortieren"
          aria-label={`${attachment.filename} verschieben`}
          disabled={busy}
          {...attributes}
          {...listeners}
        >
          <GripVertical className="h-3.5 w-3.5" />
        </button>
      </div>
      <button
        type="button"
        className="absolute right-1 top-1 inline-flex h-6 w-6 items-center justify-center rounded-md bg-white/90 text-slate-400 shadow-sm hover:bg-rose-50 hover:text-rose-600"
        title="Anhang entfernen"
        aria-label={`${attachment.filename} entfernen`}
        disabled={busy}
        onClick={() => onRemove(attachment)}
      >
        <Trash2 className="h-3.5 w-3.5" />
      </button>
    </li>
  );
}
